import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mail, Clock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please complete all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Contact error:", error);
      toast.error("Failed to send. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-28 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#C9A962] text-xs tracking-[0.3em] mb-4">
            GET IN TOUCH
          </p>
          <h1 className="font-display text-4xl sm:text-5xl text-white mb-4">
            Contact
          </h1>
          <p className="text-[#B8B8B8] max-w-xl mx-auto">
            Ready to start your transformation? Have questions about our programs? 
            Reach out and we'll respond within 24-48 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#111] border border-[#222] rounded p-8 sm:p-10" data-testid="contact-form-container">
              <h2 className="text-[#C9A962] text-xs tracking-[0.2em] mb-8">
                SEND A MESSAGE
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs text-[#666] tracking-wider mb-2 block">
                      YOUR NAME *
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="John Doe"
                      className="premium-input rounded py-3 px-4"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#666] tracking-wider mb-2 block">
                      EMAIL *
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="your@email.com"
                      className="premium-input rounded py-3 px-4"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-[#666] tracking-wider mb-2 block">
                    SUBJECT *
                  </Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => handleChange("subject", e.target.value)}
                    placeholder="Program Inquiry"
                    className="premium-input rounded py-3 px-4"
                    data-testid="contact-input-subject"
                  />
                </div>

                <div>
                  <Label className="text-xs text-[#666] tracking-wider mb-2 block">
                    MESSAGE *
                  </Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    placeholder="Tell us about your goals and how we can help..."
                    rows={5}
                    className="premium-input rounded py-3 px-4 resize-none"
                    data-testid="contact-input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-premium py-4 text-xs rounded flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? "SENDING..." : "SEND MESSAGE"}
                  <Send size={14} />
                </Button>
              </form>
            </div>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email */}
            <div className="bg-[#111] border border-[#222] rounded p-6" data-testid="contact-info-email">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#161616] rounded flex items-center justify-center flex-shrink-0">
                  <Mail size={18} className="text-[#FFB800]" />
                </div>
                <div>
                  <p className="text-xs text-[#666] tracking-wider mb-1">EMAIL</p>
                  <a
                    href="mailto:therealdealcormier@gmail.com"
                    className="text-white hover:text-[#FFB800] transition-colors"
                  >
                    therealdealcormier@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="bg-[#111] border border-[#222] rounded p-6" data-testid="contact-info-whatsapp">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#161616] rounded flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#FFB800]">
                    <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21"/>
                    <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#666] tracking-wider mb-1">WHATSAPP</p>
                  <a
                    href="https://wa.me/13103575542"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-[#FFB800] transition-colors"
                  >
                    +1 (310) 357-5542
                  </a>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-[#111] border border-[#222] rounded p-6" data-testid="contact-info-response">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#161616] rounded flex items-center justify-center flex-shrink-0">
                  <Clock size={18} className="text-[#FFB800]" />
                </div>
                <div>
                  <p className="text-xs text-[#666] tracking-wider mb-1">RESPONSE TIME</p>
                  <p className="text-white">24-48 Hours</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-[#161616] border border-[#222] rounded p-6">
              <p className="text-white text-sm font-medium mb-2">
                Ready to apply?
              </p>
              <p className="text-[#666] text-xs mb-4 leading-relaxed">
                Complete the athlete assessment for a personalized program recommendation.
              </p>
              <a href="/assessment">
                <Button className="w-full btn-outline-premium py-3 text-xs rounded">
                  START ASSESSMENT
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
